import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import EventConfigManager from '../../components/dashboard/EventConfigManager';
import { ArrowLeft, AlertTriangle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EventConfigPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(eventId || null);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(null);
  const [useDemoMode, setUseDemoMode] = useState(false);
  
  // Fetch events list
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await fetch(`${API}/events`);
        if (response.ok) {
          const data = await response.json();
          setEvents(data);
          setApiError(null);
          
          // Auto-select KCC event if no event specified
          if (!eventId) {
            const kccEvent = data.find(e => 
              e.name?.includes('KCC') || e.name?.includes('Family Sports Day')
            );
            if (kccEvent) {
              setSelectedEventId(kccEvent.id);
            }
          }
        } else {
          // API returned non-OK status (e.g., 500)
          console.error('API error:', response.status);
          setApiError(`Database connection issue (Status: ${response.status}). The system is using demo mode.`);
          setUseDemoMode(true);
          setSelectedEventId('demo');
        }
      } catch (error) {
        console.error('Error fetching events:', error);
        setApiError('Unable to connect to the server. Using demo mode.');
        setUseDemoMode(true);
        setSelectedEventId('demo');
      } finally {
        setLoading(false);
      }
    };
    
    fetchEvents();
  }, [eventId]);
  
  // Handle seed event creation
  const handleCreateEvent = async () => {
    try {
      toast.info('Creating KCC Family Sports Day event...');
      const response = await fetch(`${API}/seed/kcc-family-sports-day`, {
        method: 'POST'
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success('Event created successfully!');
        setSelectedEventId(data.event_id);
        setApiError(null);
        setUseDemoMode(false);
        // Refresh events list
        const eventsRes = await fetch(`${API}/events`);
        if (eventsRes.ok) {
          setEvents(await eventsRes.json());
        }
      } else {
        const errorText = await response.text();
        toast.error(`Failed to create event: ${response.status}. Database may be unavailable.`);
        console.error('Seed error:', errorText);
      }
    } catch (error) {
      console.error('Error creating event:', error);
      toast.error('Failed to create event. Server may be unavailable.');
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50" data-testid="event-config-page">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                onClick={() => navigate(-1)}
                className="gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </Button>
              <h1 className="text-xl font-bold text-gray-900">Event Configuration</h1>
            </div>
            
            {/* Event Selector */}
            {events.length > 0 && (
              <select
                value={selectedEventId || ''}
                onChange={e => setSelectedEventId(e.target.value)}
                className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
              >
                <option value="">Select an event...</option>
                {events.map(event => (
                  <option key={event.id} value={event.id}>
                    {event.name}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* API Error Banner */}
        {apiError && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800">Database Connection Issue</p>
              <p className="text-sm text-amber-700">{apiError}</p>
              {useDemoMode && (
                <p className="text-xs text-amber-600 mt-1">
                  You can still preview the configuration interface below. Changes will not persist until the database is restored.
                </p>
              )}
            </div>
          </div>
        )}
        
        {selectedEventId ? (
          <EventConfigManager 
            eventId={selectedEventId}
            onClose={() => navigate('/family-sports-day')}
            demoMode={useDemoMode}
          />
        ) : (
          <div className="bg-white rounded-2xl p-12 text-center shadow-sm">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">No Event Selected</h2>
            <p className="text-gray-500 mb-6">
              Select an event from the dropdown above to configure it, or create a new Family Sports Day event.
            </p>
            <Button 
              onClick={handleCreateEvent}
              className="bg-green-600 hover:bg-green-700"
            >
              Create KCC Family Sports Day
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
